/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { AdcModel, ShuttleModel } from '../../../../shared/models';
import { AdcsApiService, ShuttleApiService } from '../../../../shared/http';
import { AdcsUtilsService, NamespaceService } from '../../../../shared/services/utils';
import { BaseComponent } from '../../../../../../core/components';
import { Component } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LanguageService, LogService, UtilsService } from '../../../../../../core/services';
import { domainOrIpValidation, nameValidation } from '../../../../../../core/directives';
import { forkJoin, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-adcs.edit',
    templateUrl: './adcs.edit.component.html',
    styleUrls: ['./adcs.edit.component.scss']
})
export class AdcsEditComponent extends BaseComponent {
    /**
     * Form group name.
     */
    public formGroup: FormGroup;

    /**
     * Namespace selected key.
     */
    public selectedNamespaceKey: string;

    /**
     * Store the shuttles dropdown for use in the view.
     */
    public shuttlesDropdown: DropdownModel[] = [];

    /**
     * Namespace dropdown content.
     */
    public namespaceDropdown: DropdownModel[] = [];

    /**
     * Store the ADC ID you are editing.
     * @private
     */
    private readonly editId: number;

    /**
     * Store the list of ADCs for the selected namespace.
     * @private
     */
    private adcsList: any = [];

    /**
     * Store the list of shuttles from the API.
     * @private
     */
    private shuttleList: ShuttleModel[];

    /**
     * To store the initial IP form data.
     * @private
     */
    private initialFormData: any = {};

    constructor(
        private activatedRoute: ActivatedRoute,
        private adcsApiService: AdcsApiService,
        private adcsUtilsService: AdcsUtilsService,
        private formBuilder: FormBuilder,
        private languageService: LanguageService,
        private logService: LogService,
        private namespaceService: NamespaceService,
        private router: Router,
        private shuttleApiService: ShuttleApiService,
        private utilsService: UtilsService
    ) {
        super();

        const namespaces = this.namespaceService.namespaces;

        this.setUpForm();
        this.setUpFormSubscriber();
        this.setNamespaceDropdownContent(namespaces);

        const id = this.activatedRoute.snapshot.paramMap.get('id');
        const namespaceId = this.activatedRoute.snapshot.paramMap.get('namespaceId');
        if (id) {
            this.editId = parseInt(id, 10);
            this.setUpPageData(namespaceId, this.editId);
        } else {
            this.logService.error('AdcsEditComponent:constructor', 'No ID in url');
        }
    }

    /**
     * Set tag value in form.
     * @param event
     */
    public setTagsFormValue(event: any): void {
        this.adcsUtilsService.setTagsFormValue(this.formGroup, event);
    }

    /**
     * Update the adc.
     */
    public updateAdc(): Observable<any> {
        return new Observable<any>(observer => {
            const formValue = this.formGroup.getRawValue();
            formValue.port = formValue.port.toString();
            const id = formValue.id;
            delete formValue.id;

            this.adcsApiService.update(formValue.namespaceID, id, formValue)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        observer.next();
                        this.router.navigate(['/cm/adcs/list']).then(() => {
                        });
                    },
                    error: (error: any): void => {
                        observer.error(error);
                    }
                }).add(
                (): void => {
                    observer.complete();
                });
        });
    }

    /**
     * Set up the form for the page.
     * @private
     */
    private setUpForm(): void {
        this.formGroup = this.formBuilder.group({
            id: null,
            locked: [null],
            label: [null, [Validators.required, nameValidation]],
            sidecarID: [null],
            ipAddress: [null, [Validators.required, domainOrIpValidation]],
            port: [null, Validators.required],
            namespaceID: null,
            notes: [null, Validators.maxLength(256)],
            tags: []
        });
    }

    /**
     * Set up form subscriber.
     * @private
     */
    private setUpFormSubscriber() {
        this.formGroup.valueChanges
            .subscribe({
                next: (values: any): void => {
                    this.utilsService.debounce('formChanges', () => {
                        const formData = this.formGroup.getRawValue();
                        formData.port = formData.port.toString();
                        if (!this.utilsService.hasFormDataChanged(this.initialFormData, formData)) {
                            this.formGroup.markAsPristine();
                        } else {
                            this.formGroup.markAsDirty();
                        }

                        const selectedShuttle = this.shuttleList.find((item: ShuttleModel) => item.id === values.sidecarID);
                        if (!selectedShuttle.legacy) {
                            this.validatePortAndIpForSelectedShuttle(values.ipAddress, values.port, values.sidecarID);
                            this.validateIPinSubnet(values.ipAddress, values.sidecarID);
                        }
                    });
                }
            });

        this.formGroup.get('locked').valueChanges
            .subscribe({
                next: (): void => {
                    this.adcsUtilsService.lockForm(this.formGroup);
                }
            });

        this.formGroup.get('label').valueChanges
            .subscribe({
                next: (label: string): void => {
                    this.validateLabelInput(label);
                }
            });
    }

    /**
     * Validate label input.
     * @param label
     * @private
     */
    private validateLabelInput(label: string): void {
        const foundMatch = this.adcsList.find((adc: AdcModel) => adc.label === label && adc.id !== this.editId);
        if (foundMatch) {
            this.formGroup.get('label').setErrors({ 'duplicateValue': `${this.languageService.translate('duplicate_adc_label')}` });
        }
    }

    /**
     * Validate inputs.
     * @param ipAddress
     * @param port
     * @param shuttleId
     * @private
     */
    private validatePortAndIpForSelectedShuttle(ipAddress: string, port: number, shuttleId: any): void {
        const adcsExcludingEditId = this.adcsList.filter((item: AdcModel) => item.id !== this.editId);
        this.adcsUtilsService.validatePortAndIpForSelectedShuttle(adcsExcludingEditId, this.formGroup, ipAddress, port, shuttleId);
    }

    /**
     * Validate IP in subnet
     * @param ipAddress
     * @param shuttleId
     * @private
     */
    private validateIPinSubnet(ipAddress: string, shuttleId: number) {
        this.adcsUtilsService.validateIPinSubnet(this.shuttleList, this.formGroup, ipAddress, shuttleId);
    }

    /**
     * Set up namespace dropdown content.
     * @param namespaces
     * @private
     */
    private setNamespaceDropdownContent(namespaces: any[]): void {
        const noAllNamespaceList: DropdownModel[] = namespaces.filter(item => item.key !== 0);
        for (const namespace of noAllNamespaceList) {
            this.namespaceDropdown.push(
                <DropdownModel>{
                    key: namespace.key,
                    value: namespace.value
                }
            );
        }
    }

    /**
     * Get the most upto date list of ADCs.
     * @private
     */
    private setUpPageData(namespaceId: any, adcId: any): void {
        const apiCalls = {
            adcs: this.adcsApiService.getAll(namespaceId),
            // TODO we need to think about ADCs and Shuttles having different namespaces, setting 0 for now.
            shuttles: this.shuttleApiService.getShuttleList(0)
        };

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    if (data.adcs && data.shuttles) {
                        this.adcsList = data.adcs;
                        this.shuttleList = data.shuttles;
                        const adc = data.adcs.find((item: AdcModel) => item.id === parseInt(adcId, 10));
                        const shuttle = data.shuttles.find((item: ShuttleModel) => item.id === adc.sidecarID);
                        if (shuttle) {
                            this.setUpShuttleDropdown(shuttle);
                        }

                        this.patchDataToForm(adc);
                    } else {
                        this.logService.error('setUpPageData', 'Missing adc and shuttle data');
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('setUpPageData', 'Unable to get adc and shuttle data', error);
                }
            });
    }

    /**
     * Setup dropdown arrays object.
     * @private
     */
    private setUpShuttleDropdown(shuttle: ShuttleModel): void {
        this.shuttlesDropdown = this.adcsUtilsService.getFormattedShuttleDropdown([shuttle]);
    }

    /**
     * Patch API data to form.
     * @param adc
     * @private
     */
    private patchDataToForm(adc: AdcModel): void {
        adc.tags = adc.tags ? adc.tags : [];
        this.formGroup.patchValue(adc);
        this.initialFormData = this.utilsService.duplicateObject(this.formGroup.getRawValue());
        this.adcsUtilsService.lockForm(this.formGroup);
    }
}
